import { useEffect, useState } from "react";
import { Clock, Sparkles } from "lucide-react";
import { useBilling } from "@/hooks/useBilling";
import { useNavigate } from "react-router-dom";

export function TrialCountdownBanner() {
  const { trial } = useBilling();
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState<string>("");

  useEffect(() => {
    if (!trial?.active || !trial?.expiresAt) {
      return;
    }

    const updateTimer = () => {
      const now = new Date().getTime();
      const expiresAt = new Date(trial.expiresAt!).getTime();
      const diff = expiresAt - now;

      if (diff <= 0) {
        setTimeLeft("00:00:00");
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft(
        `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
      );
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [trial]);

  // Don't show if trial is not active or already used
  if (!trial?.active || trial?.used) {
    return null;
  }

  return (
    <div className="glass-panel border-indigo-100 bg-gradient-to-r from-indigo-50/60 to-sky-50/60">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 shadow-lg shadow-indigo-200">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900">
              Your first AR-ready product is free — premium modes free for now
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Use one product photo, generate your first 3D model, and see it live on your store. The free product has no time limit; the countdown is how long premium capture &amp; render stay free on it.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-start gap-3 md:items-end">
          <div className="flex items-center gap-2 rounded-2xl border border-indigo-200 bg-white/80 px-4 py-2 shadow-sm">
            <Clock className="h-4 w-4 text-indigo-600" />
            <span className="font-mono text-sm font-semibold text-indigo-600">
              {timeLeft} left
            </span>
          </div>
          <button
            onClick={() => navigate("/products")}
            className="rounded-2xl bg-gradient-to-r from-indigo-500 to-sky-500 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:shadow-xl"
          >
            Generate my free product
          </button>
        </div>
      </div>
    </div>
  );
}

